import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Grid,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  WorkspacePremium as PlanIcon,
  Extension as AddonsIcon,
  LockOpen as LiberationIcon,
  WarningAmber as WarningIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import AddonsCatalogTab from '../components/superadmin/AddonsCatalogTab';
import LiberationsTab from '../components/superadmin/LiberationsTab';
import StatusPill from '../components/superadmin/StatusPill';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { GLASS } from '../theme/glassTokens';

// Services
import { useAuth } from '../contexts/AuthContext';
import {
  subscriptionService,
  Organization,
  Subscription,
  SubscriptionPlan,
  LiveUsage
} from '../services/subscriptionService';
import {
  formatBRLFromCents,
  formatLimit,
  formatPlanOptionLabel,
  getApprovalContractMonthlyCents,
  getApprovalPricePerClientCents,
  getSubscriptionStatusDisplay,
  isSubscriptionCurrent,
  isSubscriptionExpired,
  UNLIMITED_THRESHOLD,
} from '../utils/subscriptionDisplay';

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
}

const cardSx = {
  border: `1px solid ${GLASS.border.outer}`,
  borderRadius: GLASS.radius.card,
  background: GLASS.surface.bg,
  boxShadow: GLASS.shadow.card,
};

const dialogPaperSx = {
  borderRadius: GLASS.radius.card,
  background: GLASS.surface.bgStrong,
  boxShadow: GLASS.shadow.card,
};

const primaryButtonSx = {
  bgcolor: GLASS.accent.orange,
  '&:hover': { bgcolor: GLASS.accent.orangeDark },
  borderRadius: GLASS.radius.button,
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const tableSx = {
  '& .MuiTableCell-head': {
    color: GLASS.text.muted,
    fontWeight: 600,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    bgcolor: GLASS.surface.bgFooter,
    borderBottom: `1px solid ${GLASS.border.light}`,
  },
  '& .MuiTableCell-body': {
    borderBottom: `1px solid ${GLASS.border.light}`,
    color: GLASS.text.body,
  },
  '& .MuiTableRow-root:last-of-type .MuiTableCell-body': { borderBottom: 'none' },
};

const tableContainerSx = {
  overflowX: 'auto',
  border: `1px solid ${GLASS.border.light}`,
  borderRadius: GLASS.radius.inner,
};

const PanelToolbar: React.FC<{ description: string; action?: React.ReactNode }> = ({ description, action }) => (
  <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
    <Typography variant="body2" sx={{ color: GLASS.text.muted, flex: '1 1 260px' }}>
      {description}
    </Typography>
    {action}
  </Box>
);

const RowActions: React.FC<{ onEdit: () => void; onDelete: () => void }> = ({ onEdit, onDelete }) => (
  <Box sx={{ display: 'inline-flex', gap: 0.5, whiteSpace: 'nowrap' }}>
    <Tooltip title="Editar">
      <IconButton size="small" onClick={onEdit} sx={{ color: GLASS.accent.orange }}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Excluir">
      <IconButton size="small" onClick={onDelete} color="error">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);

/** Barra de uso: vermelha quando passa do limite, laranja a partir de 90%, sem barra quando ilimitado. */
const UsageMeter: React.FC<{ label: string; used: number; max: number }> = ({ label, used, max }) => {
  const unlimited = max >= UNLIMITED_THRESHOLD;
  const over = !unlimited && used > max;
  const percent = unlimited || max <= 0 ? 0 : Math.min((used / max) * 100, 100);
  const fill = over ? '#dc2626' : percent >= 90 ? '#f59e0b' : GLASS.accent.orange;

  return (
    <Box sx={{ mb: 1, '&:last-of-type': { mb: 0 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="caption" sx={{ color: GLASS.text.muted }}>
          {label}
        </Typography>
        <Typography variant="caption" fontWeight={700} sx={{ color: over ? '#b91c1c' : GLASS.text.heading }}>
          {used} / {formatLimit(max)}
        </Typography>
      </Box>
      {!unlimited && (
        <Box sx={{ mt: 0.5, height: 4, borderRadius: 2, bgcolor: 'rgba(82, 86, 99, 0.1)', overflow: 'hidden' }}>
          <Box sx={{ width: `${over ? 100 : percent}%`, height: '100%', bgcolor: fill, borderRadius: 2 }} />
        </Box>
      )}
    </Box>
  );
};

const formatDate = (iso?: string | null) => (iso ? format(new Date(iso), 'dd/MM/yyyy', { locale: ptBR }) : '—');

const describeExpiry = (periodEnd: string): string => {
  const days = Math.ceil((new Date(periodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (days < 0) return `Venceu há ${Math.abs(days)} ${Math.abs(days) === 1 ? 'dia' : 'dias'}`;
  if (days === 0) return 'Vence hoje';
  if (days === 1) return 'Vence amanhã';
  return `${days} dias restantes`;
};

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);

  // Data states
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [usageByOrg, setUsageByOrg] = useState<Record<string, LiveUsage>>({});

  // Dialog states - Organizations
  const [orgDialog, setOrgDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    organization: Organization | null;
  }>({ open: false, mode: 'create', organization: null });

  const [orgForm, setOrgForm] = useState<Partial<Organization>>({
    name: '',
    email: '',
    phone: '',
    document: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'BR',
    active: true
  });

  // Dialog states - Subscriptions
  const [subDialog, setSubDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    subscription: Subscription | null;
  }>({ open: false, mode: 'create', subscription: null });

  const [subForm, setSubForm] = useState<Partial<Subscription>>({
    organization_id: '',
    plan_id: '',
    status: 'active',
    current_period_start: new Date().toISOString(),
    current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
  });

  // Dialog states - Plans
  const [planDialog, setPlanDialog] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    plan: SubscriptionPlan | null;
  }>({ open: false, mode: 'create', plan: null });

  const [planForm, setPlanForm] = useState<Partial<SubscriptionPlan>>({
    name: '',
    plan_code: '',
    tier_order: 0,
    stripe_price_id: '',
    stripe_product_id: '',
    is_enterprise_contact: false,
    amount: 0,
    currency: 'brl',
    interval: 'month',
    max_profiles: 1,
    max_clients: 1,
    max_posts_per_month: 10,
    active: true,
    features: {}
  });

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    type: 'organization' | 'subscription' | 'plan' | null;
    id: string | null;
    name: string;
  }>({ open: false, type: null, id: null, name: '' });

  // Load data
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [orgsData, subsData, plansData] = await Promise.all([
        subscriptionService.getAllOrganizations(),
        subscriptionService.getAllSubscriptions(),
        subscriptionService.getAllPlans()
      ]);

      // Contagem ao vivo por organização (subscription_usage fica parado quando a assinatura vence)
      const usageEntries = await Promise.all(
        orgsData.map(async (org) => {
          try {
            return [org.id, await subscriptionService.getLiveUsageByOrganization(org.id)] as const;
          } catch (error) {
            console.error('❌ Erro ao contar uso da organização:', org.id, error);
            return [org.id, { profiles_count: 0, clients_count: 0, posts_count: 0 }] as const;
          }
        })
      );

      setOrganizations(orgsData);
      setSubscriptions(subsData);
      setPlans(plansData);
      setUsageByOrg(Object.fromEntries(usageEntries));
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  /** Assinatura mais recente de cada organização (getAllSubscriptions já vem ordenado por created_at desc). */
  const latestSubByOrg = useMemo(() => {
    const map = new Map<string, Subscription>();
    subscriptions.forEach((s) => {
      if (!map.has(s.organization_id)) map.set(s.organization_id, s);
    });
    return map;
  }, [subscriptions]);

  const stats = useMemo(() => {
    const latest = Array.from(latestSubByOrg.values());
    const current = latest.filter((s) => isSubscriptionCurrent(s));
    const attention = latest.filter((s) => !isSubscriptionCurrent(s) && s.status !== 'canceled');
    return {
      organizations: organizations.length,
      activeOrganizations: organizations.filter((o) => o.active).length,
      current: current.length,
      attention,
      activePlans: plans.filter((p) => p.active).length,
      totalPlans: plans.length,
    };
  }, [latestSubByOrg, organizations, plans]);

  /** Precisam de atenção primeiro, depois em dia, canceladas por último. */
  const sortedSubscriptions = useMemo(() => {
    const rank = (s: Subscription) => (s.status === 'canceled' ? 2 : isSubscriptionCurrent(s) ? 1 : 0);
    return [...subscriptions].sort((a, b) => rank(a) - rank(b));
  }, [subscriptions]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Organization handlers
  const handleCreateOrg = () => {
    setOrgForm({
      name: '',
      email: '',
      phone: '',
      document: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'BR',
      active: true
    });
    setOrgDialog({ open: true, mode: 'create', organization: null });
  };

  const handleEditOrg = (org: Organization) => {
    setOrgForm(org);
    setOrgDialog({ open: true, mode: 'edit', organization: org });
  };

  const handleSaveOrg = async () => {
    setLoading(true);
    try {
      if (orgDialog.mode === 'create') {
        await subscriptionService.createOrganization(orgForm);
      } else if (orgDialog.organization) {
        await subscriptionService.updateOrganization(orgDialog.organization.id, orgForm);
      }
      await loadAllData();
      setOrgDialog({ open: false, mode: 'create', organization: null });
    } catch (error: any) {
      console.error('❌ Erro ao salvar organização:', error);
      alert(`Erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteOrg = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    setDeleteDialog({
      open: true,
      type: 'organization',
      id: orgId,
      name: org?.name || 'esta organização'
    });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.id || !deleteDialog.type) return;

    setLoading(true);
    try {
      switch (deleteDialog.type) {
        case 'organization':
          await subscriptionService.deleteOrganization(deleteDialog.id);
          break;
        case 'subscription':
          await subscriptionService.deleteSubscription(deleteDialog.id);
          break;
        case 'plan':
          await subscriptionService.deletePlan(deleteDialog.id);
          break;
      }
      await loadAllData();
      setDeleteDialog({ open: false, type: null, id: null, name: '' });
    } catch (error: any) {
      console.error('❌ Erro ao deletar:', error);
      alert(`Erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Subscription handlers
  const handleCreateSub = () => {
    setSubForm({
      organization_id: '',
      plan_id: '',
      status: 'active',
      current_period_start: new Date().toISOString(),
      current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
    });
    setSubDialog({ open: true, mode: 'create', subscription: null });
  };

  const handleEditSub = (sub: Subscription) => {
    // Filtrar apenas campos editáveis (remover organization e plan que são JOINs)
    const editableFields: Partial<Subscription> = {
      organization_id: sub.organization_id,
      plan_id: sub.plan_id,
      status: sub.status,
      current_period_start: sub.current_period_start,
      current_period_end: sub.current_period_end,
      cancel_at_period_end: sub.cancel_at_period_end,
      canceled_at: sub.canceled_at,
      trial_start: sub.trial_start,
      trial_end: sub.trial_end,
      stripe_subscription_id: sub.stripe_subscription_id,
      stripe_customer_id: sub.stripe_customer_id
    };
    setSubForm(editableFields);
    setSubDialog({ open: true, mode: 'edit', subscription: sub });
  };

  const handleSaveSub = async () => {
    setLoading(true);
    try {
      // Filtrar apenas campos editáveis (remover campos undefined e JOINs)
      const cleanForm: Partial<Subscription> = {
        organization_id: subForm.organization_id,
        plan_id: subForm.plan_id,
        status: subForm.status,
        current_period_start: subForm.current_period_start,
        current_period_end: subForm.current_period_end,
        cancel_at_period_end: subForm.cancel_at_period_end,
        canceled_at: subForm.canceled_at || null,
        trial_start: subForm.trial_start || null,
        trial_end: subForm.trial_end || null,
        stripe_subscription_id: subForm.stripe_subscription_id || null,
        stripe_customer_id: subForm.stripe_customer_id || null
      };

      // Remover campos undefined
      Object.keys(cleanForm).forEach(key => {
        if (cleanForm[key as keyof Subscription] === undefined) {
          delete cleanForm[key as keyof Subscription];
        }
      });

      if (subDialog.mode === 'create') {
        await subscriptionService.createSubscription(cleanForm);
      } else if (subDialog.subscription) {
        await subscriptionService.updateSubscription(subDialog.subscription.id, cleanForm);
      }
      await loadAllData();
      setSubDialog({ open: false, mode: 'create', subscription: null });
    } catch (error: any) {
      console.error('❌ Erro ao salvar subscription:', error);
      alert(`Erro: ${error.message || 'Erro ao salvar subscription'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSub = (subId: string) => {
    const sub = subscriptions.find(s => s.id === subId);
    const orgName = (sub?.organization as any)?.name || 'esta subscription';
    setDeleteDialog({
      open: true,
      type: 'subscription',
      id: subId,
      name: orgName
    });
  };

  // Plan handlers
  const handleCreatePlan = () => {
    setPlanForm({
      name: '',
      plan_code: '',
      tier_order: (plans.length > 0 ? Math.max(...plans.map(p => p.tier_order || 0)) + 1 : 1),
      stripe_price_id: '',
      stripe_product_id: '',
      is_enterprise_contact: false,
      amount: 0,
      currency: 'brl',
      interval: 'month',
      max_profiles: 1,
      max_clients: 1,
      max_posts_per_month: 10,
      active: true,
      features: {}
    });
    setPlanDialog({ open: true, mode: 'create', plan: null });
  };

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setPlanForm(plan);
    setPlanDialog({ open: true, mode: 'edit', plan });
  };

  const handleSavePlan = async () => {
    setLoading(true);
    try {
      if (planDialog.mode === 'create') {
        await subscriptionService.createPlan(planForm);
      } else if (planDialog.plan) {
        await subscriptionService.updatePlan(planDialog.plan.id, planForm);
      }
      await loadAllData();
      setPlanDialog({ open: false, mode: 'create', plan: null });
    } catch (error: any) {
      console.error('❌ Erro ao salvar plano:', error);
      alert(`Erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePlan = (planId: string) => {
    const plan = plans.find(p => p.id === planId);
    setDeleteDialog({
      open: true,
      type: 'plan',
      id: planId,
      name: plan?.name || 'este plano'
    });
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/super-admin/login');
  };

  const renderPlanPrice = (plan: SubscriptionPlan) => {
    if (plan.plan_code === 'APROVACAO_ONLY') {
      const unit = getApprovalPricePerClientCents(plan);
      return (
        <>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {formatBRLFromCents(getApprovalContractMonthlyCents(plan.max_clients, unit))}/mês
          </Typography>
          <Typography variant="caption" sx={{ color: GLASS.text.muted, whiteSpace: 'nowrap' }}>
            {formatBRLFromCents(unit)} × {plan.max_clients} clientes
          </Typography>
        </>
      );
    }
    if (plan.is_enterprise_contact) return <StatusPill label="A consultar" tone="warning" />;
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
        {formatBRLFromCents(plan.amount)}
      </Typography>
    );
  };

  const statCards = [
    {
      icon: <BusinessIcon />,
      value: stats.organizations,
      label: 'Organizações',
      caption: `${stats.activeOrganizations} ativas`,
      tone: 'default' as const,
    },
    {
      icon: <CreditCardIcon />,
      value: stats.current,
      label: 'Assinaturas em dia',
      caption: 'Ativas e dentro da vigência',
      tone: 'default' as const,
    },
    {
      icon: <WarningIcon />,
      value: stats.attention.length,
      label: 'Precisam de atenção',
      caption:
        stats.attention.length > 0
          ? stats.attention.map((s) => (s.organization as any)?.name || '—').join(', ')
          : 'Nenhuma assinatura vencida',
      tone: stats.attention.length > 0 ? ('alert' as const) : ('default' as const),
    },
    {
      icon: <PlanIcon />,
      value: stats.activePlans,
      label: 'Planos ativos',
      caption: `${stats.totalPlans} cadastrados`,
      tone: 'default' as const,
    },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: 1440, mx: 'auto', boxSizing: 'border-box', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 2.5, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2.5, md: 3.5 }, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: GLASS.accent.orange, fontWeight: 700, letterSpacing: '0.12em', lineHeight: 1.4 }}>
            INSYT
          </Typography>
          <Typography variant="h4" fontWeight={700} sx={{ color: GLASS.text.heading, fontSize: { xs: '1.5rem', md: '2rem' }, lineHeight: 1.2 }}>
            Super Admin
          </Typography>
          <Typography variant="body2" sx={{ color: GLASS.text.muted, mt: 0.5 }}>
            Organizações, assinaturas, planos e liberações manuais
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            onClick={loadAllData}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GLASS.border.outer, color: GLASS.text.heading, borderRadius: GLASS.radius.button, '&:hover': { borderColor: GLASS.accent.orange, bgcolor: 'rgba(247, 66, 17, 0.04)' } }}
          >
            Atualizar
          </Button>
          <Button
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            variant="text"
            color="error"
            sx={{ borderRadius: GLASS.radius.button }}
          >
            Sair
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: { xs: 2.5, md: 3.5 } }}>
        {statCards.map((item) => {
          const alert = item.tone === 'alert';
          return (
            <Grid item xs={6} lg={3} key={item.label}>
              <Box
                sx={{
                  ...cardSx,
                  height: '100%',
                  boxSizing: 'border-box',
                  p: { xs: 1.75, md: 2.5 },
                  borderColor: alert ? 'rgba(220, 38, 38, 0.35)' : GLASS.border.outer,
                  bgcolor: alert ? 'rgba(239, 68, 68, 0.04)' : GLASS.surface.bg,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: alert ? '#b91c1c' : GLASS.text.muted }}>
                    {item.label}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      color: alert ? '#dc2626' : GLASS.accent.orange,
                      bgcolor: alert ? 'rgba(239, 68, 68, 0.1)' : 'rgba(247, 66, 17, 0.08)',
                      borderRadius: GLASS.radius.inner,
                      p: 0.75,
                      '& svg': { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                  </Box>
                </Box>
                <Typography sx={{ mt: 1, fontSize: { xs: '1.75rem', md: '2.125rem' }, fontWeight: 700, lineHeight: 1.1, color: alert ? '#b91c1c' : GLASS.text.heading }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: GLASS.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.caption}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* Tabs */}
      <Paper elevation={0} sx={{ ...cardSx, width: '100%', overflow: 'hidden' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: { xs: 0.5, md: 1.5 },
            borderBottom: `1px solid ${GLASS.border.light}`,
            '& .MuiTab-root': { minHeight: 56, textTransform: 'none', fontWeight: 600, fontSize: '0.9rem', color: GLASS.text.muted },
            '& .Mui-selected': { color: `${GLASS.accent.orange} !important` },
            '& .MuiTabs-indicator': { backgroundColor: GLASS.accent.orange, height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          <Tab icon={<BusinessIcon fontSize="small" />} iconPosition="start" label="Organizações" />
          <Tab icon={<CreditCardIcon fontSize="small" />} iconPosition="start" label="Assinaturas" />
          <Tab icon={<PlanIcon fontSize="small" />} iconPosition="start" label="Planos" />
          <Tab icon={<AddonsIcon fontSize="small" />} iconPosition="start" label="Add-ons" />
          <Tab icon={<LiberationIcon fontSize="small" />} iconPosition="start" label="Liberações" />
        </Tabs>

        {/* Tab 1: Organizations */}
        <TabPanel value={currentTab} index={0}>
          <PanelToolbar
            description="Todas as organizações cadastradas, com o plano e a situação da assinatura atual."
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOrg} sx={primaryButtonSx}>
                Nova organização
              </Button>
            }
          />

          <TableContainer sx={tableContainerSx}>
            <Table sx={{ ...tableSx, minWidth: 820 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Organização</TableCell>
                  <TableCell>Modo</TableCell>
                  <TableCell>Plano</TableCell>
                  <TableCell>Assinatura</TableCell>
                  <TableCell>Clientes</TableCell>
                  <TableCell>Criada em</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.map((org) => {
                  const sub = latestSubByOrg.get(org.id);
                  const plan = sub?.plan as SubscriptionPlan | undefined;
                  const statusDisplay = sub ? getSubscriptionStatusDisplay(sub.status, sub.current_period_end) : null;
                  const clientsUsed = usageByOrg[org.id]?.clients_count ?? 0;
                  const clientsMax = plan?.max_clients ?? 0;
                  const overClients = !!plan && clientsMax < UNLIMITED_THRESHOLD && clientsUsed > clientsMax;
                  return (
                    <TableRow key={org.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: GLASS.text.heading }}>
                          {org.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: GLASS.text.muted }}>
                          {org.email || 'Sem e-mail'}
                          {!org.active && ' · Inativa'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {org.product_mode === 'approval_only' ? (
                          <StatusPill label="Só aprovação" tone="accent" />
                        ) : (
                          <StatusPill label="Completo" tone="neutral" />
                        )}
                      </TableCell>
                      <TableCell>{plan?.name || '—'}</TableCell>
                      <TableCell>
                        {statusDisplay ? (
                          <Box>
                            <StatusPill label={statusDisplay.label} tone={statusDisplay.tone} />
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: GLASS.text.muted }}>
                              até {formatDate(sub!.current_period_end)}
                            </Typography>
                          </Box>
                        ) : (
                          <StatusPill label="Sem assinatura" tone="warning" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: overClients ? '#b91c1c' : GLASS.text.heading, whiteSpace: 'nowrap' }}>
                          {clientsUsed}
                          {plan && ` / ${formatLimit(clientsMax)}`}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(org.created_at)}</TableCell>
                      <TableCell align="right">
                        <RowActions onEdit={() => handleEditOrg(org)} onDelete={() => handleDeleteOrg(org.id)} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 2: Subscriptions */}
        <TabPanel value={currentTab} index={1}>
          <PanelToolbar
            description="Uso contado ao vivo. Vencidas aparecem primeiro: a organização fica bloqueada até renovar (aba Liberações)."
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateSub} sx={primaryButtonSx}>
                Nova assinatura
              </Button>
            }
          />

          <TableContainer sx={tableContainerSx}>
            <Table sx={{ ...tableSx, minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Organização</TableCell>
                  <TableCell>Plano</TableCell>
                  <TableCell>Situação</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Uso / limites</TableCell>
                  <TableCell>Vigência</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedSubscriptions.map((sub) => {
                  const plan = sub.plan as SubscriptionPlan | undefined;
                  const usage = usageByOrg[sub.organization_id];
                  const statusDisplay = getSubscriptionStatusDisplay(sub.status, sub.current_period_end);
                  const expired = isSubscriptionExpired(sub.current_period_end);
                  const isApproval = plan?.plan_code === 'APROVACAO_ONLY';

                  return (
                    <TableRow key={sub.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: GLASS.text.heading }}>
                          {(sub.organization as any)?.name || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{plan?.name || '—'}</Typography>
                        {isApproval && plan && (
                          <Typography variant="caption" sx={{ color: GLASS.text.muted, whiteSpace: 'nowrap' }}>
                            {formatBRLFromCents(getApprovalContractMonthlyCents(plan.max_clients, getApprovalPricePerClientCents(plan)))}/mês
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusPill label={statusDisplay.label} tone={statusDisplay.tone} />
                      </TableCell>
                      <TableCell>
                        <UsageMeter label="Clientes" used={usage?.clients_count ?? 0} max={plan?.max_clients ?? 0} />
                        <UsageMeter label="Usuários" used={usage?.profiles_count ?? 0} max={plan?.max_profiles ?? 0} />
                        <UsageMeter label="Posts no mês" used={usage?.posts_count ?? 0} max={plan?.max_posts_per_month ?? 0} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ color: expired ? '#b91c1c' : GLASS.text.heading, whiteSpace: 'nowrap' }}>
                          {formatDate(sub.current_period_end)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: GLASS.text.muted, whiteSpace: 'nowrap' }}>
                          {describeExpiry(sub.current_period_end)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <RowActions onEdit={() => handleEditSub(sub)} onDelete={() => handleDeleteSub(sub.id)} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 3: Plans */}
        <TabPanel value={currentTab} index={2}>
          <PanelToolbar
            description="Planos de assinatura. Alterar limites aqui vale para todas as organizações do plano."
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreatePlan} sx={primaryButtonSx}>
                Novo plano
              </Button>
            }
          />

          <TableContainer sx={tableContainerSx}>
            <Table size="small" sx={{ ...tableSx, minWidth: 900, '& .MuiTableCell-root': { py: 1.25 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Tier</TableCell>
                  <TableCell>Plano</TableCell>
                  <TableCell>Preço</TableCell>
                  <TableCell>Usuários</TableCell>
                  <TableCell>Clientes</TableCell>
                  <TableCell>Posts/mês</TableCell>
                  <TableCell>Stripe</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id} hover>
                    <TableCell>{plan.tier_order ?? '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ color: GLASS.text.heading }}>
                        {plan.name}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: GLASS.text.muted }}>
                        {plan.plan_code || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderPlanPrice(plan)}</TableCell>
                    <TableCell>{formatLimit(plan.max_profiles)}</TableCell>
                    <TableCell>{formatLimit(plan.max_clients)}</TableCell>
                    <TableCell>{formatLimit(plan.max_posts_per_month)}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {plan.stripe_price_id || plan.stripe_product_id ? (
                        <Tooltip title={`${plan.stripe_price_id || '—'} · ${plan.stripe_product_id || '—'}`}>
                          <Typography
                            variant="caption"
                            sx={{ display: 'block', fontFamily: 'monospace', color: GLASS.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {plan.stripe_price_id || plan.stripe_product_id}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" sx={{ color: GLASS.text.muted }}>
                          Manual
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {plan.plan_code === 'APROVACAO_ONLY' ? (
                        <StatusPill label="Só aprovação" tone="accent" />
                      ) : plan.is_enterprise_contact ? (
                        <StatusPill label="Contato" tone="info" />
                      ) : (
                        <StatusPill label="Self-service" tone="neutral" />
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusPill label={plan.active ? 'Ativo' : 'Inativo'} tone={plan.active ? 'success' : 'neutral'} />
                    </TableCell>
                    <TableCell align="right">
                      <RowActions onEdit={() => handleEditPlan(plan)} onDelete={() => handleDeletePlan(plan.id)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Tab 4: Add-ons (Catálogo) */}
        <TabPanel value={currentTab} index={3}>
          <AddonsCatalogTab />
        </TabPanel>

        {/* Tab 5: Liberações */}
        <TabPanel value={currentTab} index={4}>
          <LiberationsTab
            organizations={organizations}
            plans={plans}
            onAnyChange={loadAllData}
          />
        </TabPanel>
      </Paper>

      {/* Dialog: Organization */}
      <Dialog open={orgDialog.open} onClose={() => setOrgDialog({ open: false, mode: 'create', organization: null })} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle>
          {orgDialog.mode === 'create' ? 'Nova organização' : 'Editar organização'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={orgForm.name}
            onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={orgForm.email}
            onChange={(e) => setOrgForm({ ...orgForm, email: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Telefone"
            value={orgForm.phone}
            onChange={(e) => setOrgForm({ ...orgForm, phone: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="CNPJ/CPF"
            value={orgForm.document}
            onChange={(e) => setOrgForm({ ...orgForm, document: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Endereço"
            value={orgForm.address}
            onChange={(e) => setOrgForm({ ...orgForm, address: e.target.value })}
            margin="normal"
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Cidade"
                value={orgForm.city}
                onChange={(e) => setOrgForm({ ...orgForm, city: e.target.value })}
                margin="normal"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Estado"
                value={orgForm.state}
                onChange={(e) => setOrgForm({ ...orgForm, state: e.target.value })}
                margin="normal"
              />
            </Grid>
          </Grid>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              value={orgForm.active ? 'active' : 'inactive'}
              label="Status"
              onChange={(e) => setOrgForm({ ...orgForm, active: e.target.value === 'active' })}
            >
              <MenuItem value="active">Ativa</MenuItem>
              <MenuItem value="inactive">Inativa</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrgDialog({ open: false, mode: 'create', organization: null })}>
            Cancelar
          </Button>
          <Button onClick={handleSaveOrg} variant="contained" disabled={loading} sx={primaryButtonSx}>
            {loading ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Subscription */}
      <Dialog open={subDialog.open} onClose={() => setSubDialog({ open: false, mode: 'create', subscription: null })} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle>
          {subDialog.mode === 'create' ? 'Nova assinatura' : 'Editar assinatura'}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Organização</InputLabel>
            <Select
              value={subForm.organization_id}
              label="Organização"
              onChange={(e) => setSubForm({ ...subForm, organization_id: e.target.value })}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Plano</InputLabel>
            <Select
              value={subForm.plan_id}
              label="Plano"
              onChange={(e) => setSubForm({ ...subForm, plan_id: e.target.value })}
            >
              {plans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {formatPlanOptionLabel(plan)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Status</InputLabel>
            <Select
              value={subForm.status}
              label="Status"
              onChange={(e) => setSubForm({ ...subForm, status: e.target.value })}
            >
              <MenuItem value="active">Ativa</MenuItem>
              <MenuItem value="canceled">Cancelada</MenuItem>
              <MenuItem value="past_due">Vencida</MenuItem>
              <MenuItem value="trialing">Teste</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Início do Período"
            type="datetime-local"
            value={subForm.current_period_start ? new Date(subForm.current_period_start).toISOString().slice(0, 16) : ''}
            onChange={(e) => setSubForm({ ...subForm, current_period_start: new Date(e.target.value).toISOString() })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            label="Fim do Período"
            type="datetime-local"
            value={subForm.current_period_end ? new Date(subForm.current_period_end).toISOString().slice(0, 16) : ''}
            onChange={(e) => setSubForm({ ...subForm, current_period_end: new Date(e.target.value).toISOString() })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialog({ open: false, mode: 'create', subscription: null })}>
            Cancelar
          </Button>
          <Button onClick={handleSaveSub} variant="contained" disabled={loading} sx={primaryButtonSx}>
            {loading ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Plan */}
      <Dialog open={planDialog.open} onClose={() => setPlanDialog({ open: false, mode: 'create', plan: null })} maxWidth="md" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle>
          {planDialog.mode === 'create' ? 'Novo plano' : 'Editar plano'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Code (plan_code)"
                value={planForm.plan_code || ''}
                onChange={(e) => setPlanForm({ ...planForm, plan_code: e.target.value.toUpperCase() })}
                margin="normal"
                required
                helperText="STARTER, BASIC, PRO, BUSINESS, ENTERPRISE"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                fullWidth
                label="Nome público"
                value={planForm.name}
                onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                label="Tier (ordem)"
                type="number"
                value={planForm.tier_order ?? 0}
                onChange={(e) => setPlanForm({ ...planForm, tier_order: parseInt(e.target.value) || 0 })}
                margin="normal"
                helperText="Ordem crescente"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Preço (em centavos)"
                type="number"
                value={planForm.amount}
                onChange={(e) => setPlanForm({ ...planForm, amount: parseInt(e.target.value) || 0 })}
                margin="normal"
                required
                disabled={!!planForm.is_enterprise_contact}
                helperText={
                  planForm.is_enterprise_contact
                    ? 'Plano por contato — valor ignorado'
                    : `Equivale a R$ ${((Number(planForm.amount) || 0) / 100).toFixed(2).replace('.', ',')}`
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Intervalo</InputLabel>
                <Select
                  value={planForm.interval}
                  label="Intervalo"
                  onChange={(e) => setPlanForm({ ...planForm, interval: e.target.value })}
                >
                  <MenuItem value="month">Mensal</MenuItem>
                  <MenuItem value="year">Anual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stripe Price ID"
                value={planForm.stripe_price_id || ''}
                onChange={(e) => setPlanForm({ ...planForm, stripe_price_id: e.target.value })}
                margin="normal"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Stripe Product ID"
                value={planForm.stripe_product_id || ''}
                onChange={(e) => setPlanForm({ ...planForm, stripe_product_id: e.target.value })}
                margin="normal"
                inputProps={{ style: { fontFamily: 'monospace' } }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Máx. usuários"
                type="number"
                value={planForm.max_profiles}
                onChange={(e) => setPlanForm({ ...planForm, max_profiles: parseInt(e.target.value) || 0 })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Máx. clientes"
                type="number"
                value={planForm.max_clients}
                onChange={(e) => setPlanForm({ ...planForm, max_clients: parseInt(e.target.value) || 0 })}
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Máx. posts / mês"
                type="number"
                value={planForm.max_posts_per_month}
                onChange={(e) => setPlanForm({ ...planForm, max_posts_per_month: parseInt(e.target.value) || 0 })}
                margin="normal"
                required
                helperText="999999 = ilimitado"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={planForm.active ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) => setPlanForm({ ...planForm, active: e.target.value === 'active' })}
                >
                  <MenuItem value="active">Ativo</MenuItem>
                  <MenuItem value="inactive">Inativo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', mt: { xs: 0, sm: 1 } }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!planForm.is_enterprise_contact}
                    onChange={(e) => setPlanForm({ ...planForm, is_enterprise_contact: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Plano "A consultar" (contato)</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Oculta o preço e direciona o CTA para WhatsApp
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlanDialog({ open: false, mode: 'create', plan: null })}>
            Cancelar
          </Button>
          <Button onClick={handleSavePlan} variant="contained" disabled={loading} sx={primaryButtonSx}>
            {loading ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, id: null, name: '' })}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir <strong>{deleteDialog.name}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: null, id: null, name: '' })}>
            Cancelar
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminDashboard;
